import xl from 'excel4node';
import { saveAs } from 'file-saver';

import accessoriesSection from './AccessoriesSection';
import shadesSection from './ShadesSection';
import fabricSamplesSection from './FabricSamplesSection';
import awningsSection, { getAwningSensors } from './AwningsSection';
import apiCalls from '../PowerShadesAPIFunctions';
import { isFailedApiCall } from '../psUtil';
import { normalizeAwningProperties } from '../Quote/awningConfiguratorUtils';

const SENSOR_PROPERTY_NUMBER = 13;

/**
 * Every sensor the awning configurator can offer, so the export gets one true/false column per
 * sensor. Falls back to whatever the quote's awnings actually have selected if the option catalog
 * can't be reached.
 */
const getAwningSensorOptions = async (awnings) => {
	try {
		const optionsResp = await apiCalls.getAwningOptions();

		if (!isFailedApiCall(optionsResp)) {
			const sensorProperty = normalizeAwningProperties(optionsResp.data)
				.find((property) => property.property_number === SENSOR_PROPERTY_NUMBER);

			if (sensorProperty) {
				return [...sensorProperty.options]
					.sort((o1, o2) => o1.index - o2.index)
					.map((option) => option.value);
			}
		}
	} catch {
		// Fall through to the sensors already on the quote.
	}

	return awnings.reduce((acc, awning) => {
		getAwningSensors(awning).forEach((sensor) => {
			if (!acc.includes(sensor)) acc.push(sensor);
		});

		return acc;
	}, [] as string[]);
};

function addressBlock({ workbook, worksheet, startRow, label, address }) {
	const ws = worksheet;

	let row = startRow;

	const HEADER_STYLE = workbook.createStyle({
		font: {
			size: 16,
			bold: true,
		},
	});

	ws.cell(row++, 1)
		.string(label)
		.style(HEADER_STYLE);

	ws.cell(row, 1).string('Company');

	ws.cell(row++, 2).string(address?.company_name ?? "");

	ws.cell(row, 1).string('Name');

	ws.cell(row++, 2).string(`${address?.first_name || ''} ${address?.last_name || ''}`);

	ws.cell(row, 1).string('Address');

	ws.cell(row++, 2).string(address?.address || '');

	ws.cell(row, 1).string('Address Line 2');

	ws.cell(row++, 2).string(address?.address2 || '');

	ws.cell(row, 1).string('State/Province');

	ws.cell(row++, 2).string(address?.state || '');

	ws.cell(row, 1).string('City');

	ws.cell(row++, 2).string(address?.city || '');

	ws.cell(row, 1).string('Zip/Postal Code');

	ws.cell(row++, 2).string(address?.zip ? `${address?.zip}` : '');

	ws.cell(row, 1).string('Phone');

	ws.cell(row++, 2).string(address?.phone || '');

	ws.cell(row, 1).string('Email');

	ws.cell(row++, 2).string(address?.email || '');
}

function overviewWorksheet({
	workbook,
	worksheet,
	quote,
	quoteData,
	userMultiplier,
	hidePrice,
	currencyData,
	isAwningQuote,
	awnings,
	sensorOptions,
}) {
	const ws = worksheet;

	let currentLine = 1;

	ws.cell(currentLine, 1).string('Date Created');

	ws.cell(currentLine++, 2).string(quote.created || '');

	ws.cell(currentLine, 1).string(quote.is_order ? 'Order #' : 'Quote #');

	ws.cell(currentLine++, 2).string(quote.ID ? `${quote.ID}` : '');

	ws.cell(currentLine, 1).string('Title');

	ws.cell(currentLine++, 2).string(quoteData.title);

	ws.cell(currentLine, 1).string('Notes');

	ws.cell(currentLine++, 2).string(quoteData.notes || '');

	addressBlock({
		workbook,
		worksheet: ws,
		label: 'Shipping Address',
		address: quote.shipping_address,
		startRow: ++currentLine,
	});

	currentLine = 17;

	addressBlock({
		workbook,
		worksheet: ws,
		label: 'Billing Address',
		address: quote.billing_address,
		startRow: currentLine,
	});

	currentLine = 28;

	addressBlock({
		workbook,
		worksheet: ws,
		label: 'Property Address',
		address: quote.property_address,
		startRow: currentLine,
	});

	currentLine = 40;

	// Awning quotes can't hold shades, accessories or fabric samples, so those sections would only
	// ever be empty headers -- the awnings take their place instead.
	if (isAwningQuote) {
		// BEGIN AWNINGS
		currentLine = awningsSection({
			workbook,
			worksheet: ws,
			awnings,
			sensorOptions,
			row: currentLine,
			hidePrice,
			currencyData,
		});

		return;
	}

	// BEGIN SHADES
	currentLine = shadesSection({
		workbook,
		worksheet: ws,
		quote,
		userMultiplier,
		row: currentLine,
		hidePrice,
		currencyData,
	});

	// BEGIN ACCESSORIES
	currentLine = accessoriesSection({
		workbook,
		worksheet: ws,
		quote,
		quoteData,
		userMultiplier,
		row: currentLine,
		hidePrice,
		currencyData,
	});

	// BEGIN FABRIC SAMPLES
	currentLine = fabricSamplesSection({
		workbook,
		worksheet: ws,
		quoteData,
		row: ++currentLine,
	});
}

const exportQuote = async (quoteStore, assemblyList, multiplier) => {
	const quote = {
		shades: assemblyList,
		...quoteStore,
	};
	const quoteData = quote;
	const userMultiplier = {
		multiplier
	};

	const currencyData = {
		ISO: userMultiplier.multiplier.currency,
		// MSRP
		multiplier: userMultiplier.multiplier.currency_multiplier * 1,
		symbol: userMultiplier.multiplier.symbol ?? '$',
	};

	// Create a new instance of a Workbook class
	const workbook = new xl.Workbook();

	const hidePrice = false;
	const { quoteType } = quote;

	const prefix = (() => {
		switch (quoteType) {
			case 'CEDIA':
				return `PS`;
			case 'RMA':
				return `RMA`;
			case 'Rework':
				return `RWK`;
			case 'Demo':
				return `DEMO`;
			case 'BidSpec':
				return `BID`;
			case 'Internal':
				return `INT`;
			default:
				return `PS`;
		}
	})();

	const isAwningQuote = (quote.product_category ?? 'roller_shades') === 'awnings';
	const awnings = isAwningQuote ? (quote.awnings ?? []) : [];
	const sensorOptions = isAwningQuote ? await getAwningSensorOptions(awnings) : [];

	// Add Worksheets to the workbook
	overviewWorksheet({
		workbook,
		worksheet: workbook.addWorksheet('Overview'),
		quote,
		quoteData,
		userMultiplier,
		hidePrice,
		currencyData,
		isAwningQuote,
		awnings,
		sensorOptions,
	});

	if (isAwningQuote) {
		awningsSection({
			workbook,
			worksheet: workbook.addWorksheet('Awnings'),
			awnings,
			sensorOptions,
			row: 1,
			hidePrice,
			currencyData,
		});
	} else {
		shadesSection({
			workbook,
			worksheet: workbook.addWorksheet('Shades'),
			quote,
			userMultiplier,
			row: 1,
			hidePrice,
			currencyData,
		});
		accessoriesSection({
			workbook,
			worksheet: workbook.addWorksheet('Accessories'),
			quote,
			quoteData,
			userMultiplier,
			row: 1,
			hidePrice,
			currencyData,
		});
		fabricSamplesSection({
			workbook,
			worksheet: workbook.addWorksheet('Fabric Samples'),
			quoteData,
			row: 1,
		});
	}

	workbook.writeToBuffer().then((result) => {
		const blob = new Blob([result.buffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		});

		saveAs(blob, `${quote.is_order ? 'Order' : 'Quote'} - ${quote.display_id}.xlsx`);
	});
};

export default exportQuote;
